import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface FinancialTableEntry {
  category: string;
  value: string | number;
  _id?: string;
}

export interface BreakEvenCashFlow {
  labels: string[];
  data: number[];
}

export interface RevenueCostDataset {
  label: string;
  data: number[];
  _id?: string;
}

export interface RevenueCost {
  labels: string[];
  datasets: RevenueCostDataset[];
}

export interface CO2SavingsDataset {
  label: string;
  data: number[];
  _id?: string;
}

export interface CO2Savings {
  labels: string[];
  datasets: CO2SavingsDataset[];
}

export interface EstimatorData {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  clientId: string | null;
  reportName: string | null;
  location?: string | null;
  netProfit: number;
  financialTable: FinancialTableEntry[];
  breakEvenCashFlow: BreakEvenCashFlow;
  revenueCost: RevenueCost;
  co2Savings: CO2Savings;
  utilization: number;
  utilityRate: number;
  timestamp?: string;
}

export interface EstimatorResponse {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  clientId: string;
  reportName: string;
  location?: string;
  netProfit: number;
  financialTable: FinancialTableEntry[];
  breakEvenCashFlow: BreakEvenCashFlow;
  revenueCost: RevenueCost;
  co2Savings: CO2Savings;
  utilization: number;
  utilityRate: number;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmbeddedUserReport {
  firstName: string;
  lastName: string;
  email: string;
  clientId: string;
  estimator: EstimatorResponse;
}

export interface EmbeddedUserReportsData {
  reports: EmbeddedUserReport[];
  total: number;
  page: number;
  pages: number;
}

export interface EmbeddedUserReportsApiResponse {
  success: boolean;
  message: string;
  data?: EmbeddedUserReportsData;
}

export interface EstimatorApiResponse {
  success: boolean;
  message: string;
  data?: {
    estimator: EstimatorResponse;
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// CREATE EMBEDDED ESTIMATOR ACTION
export const createEmbeddedEstimator = createAsyncThunk<
  EstimatorApiResponse,
  EstimatorData,
  { rejectValue: string }
>(
  "estimator/createEmbeddedEstimator",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createApiClient.post<EstimatorApiResponse>(
        "/estimator/embedded",
        formData
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to create estimator report"
        );
        return rejectWithValue(
          response.data.message || "Failed to create estimator report"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as EstimatorApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET EMBEDDED USER REPORTS ACTION
export const getEmbeddedUserReports = createAsyncThunk<
  EmbeddedUserReportsApiResponse,
  { userId: string; page?: number; limit?: number },
  { rejectValue: string }
>(
  "estimator/getEmbeddedUserReports",
  async ({ userId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await createApiClient.get<
        EmbeddedUserReportsApiResponse
      >(`/estimator/embedded-user-reports/${userId}?${params.toString()}`);

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to fetch embedded user reports"
        );
        return rejectWithValue(
          response.data.message || "Failed to fetch embedded user reports"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as EmbeddedUserReportsApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
